// frontend/src/components/WorkshopSidebar.js  (replacement)
// Grouped nav (Atelier / Documents / Gestion), stroke icons instead of emoji,
// 1Parc chrome mark, French throughout. NavLink instead of manual navigate().

import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import IparcMark from './IparcMark';
import {
  IconHome, IconWrench, IconDoc, IconClipboard, IconCard, IconBox,
  IconHelp, IconLogout, IconCar,
} from './icons/Icons';

const IconWalkIn = ({ className = 'w-[17px] h-[17px]' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 4a2 2 0 1 1-4 0 2 2 0 0 1 4 0M11 8v12M11 12l5 2M11 20l-3-3" />
  </svg>
);
const IconLedger = ({ className = 'w-[17px] h-[17px]' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round"><path d="M4 5h16v15H4zM4 9h16M9 9v11" /></svg>
);
const IconBars = ({ className = 'w-[17px] h-[17px]' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round"><path d="M4 19V9M10 19V5M16 19v-7M4 19h16" /></svg>
);
// A till slip with a torn bottom edge — reads as "reçu" rather than "facture",
// which already owns IconCard here.
const IconReceipt = ({ className = 'w-[17px] h-[17px]' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3h12v18l-2-1.5L14 21l-2-1.5L10 21l-2-1.5L6 21z" />
    <path d="M9 8h6M9 12h6" />
  </svg>
);
// A key passing between two hands — the handover, not another document icon.
const IconHandover = ({ className = 'w-[17px] h-[17px]' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="9" r="3" /><path d="M10.5 11L16 16.5M14 14.5l1.5 1.5M12.5 13l1.5 1.5" />
    <path d="M3 20h18" />
  </svg>
);
const IconTag = ({ className = 'w-[17px] h-[17px]' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h7l9 9-7 7-9-9z" /><path d="M8.5 8.5h.01" />
  </svg>
);
const IconUser = ({ className = 'w-[17px] h-[17px]' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round">
    <circle cx="12" cy="8.5" r="3.5" /><path d="M5 20a7 7 0 0 1 14 0" />
  </svg>
);

export default function WorkshopSidebar({
  className = '', pendingQuotes = 0, unpaidInvoices = 0, supplierQuotes = 0, vehicleCount, onLogout,
}) {
  const { user } = useAuth();

  const sections = [
    {
      title: 'Atelier',
      items: [
        { label: 'Tableau de bord',        icon: IconHome,      path: '/workshop' },
        { label: 'Demandes',               icon: IconClipboard, path: '/workshop/requests' },
        { label: 'Client sans rendez-vous',icon: IconWalkIn,    path: '/workshop/walk-in' },
        { label: 'Fiche de réception',     icon: IconClipboard, path: '/workshop/reception-checklist' },
        { label: 'Saisir un entretien',    icon: IconDoc,       path: '/workshop/log-maintenance' },
        { label: 'Catalogue pièces',       icon: IconWrench,    path: '/workshop/catalog' },
      ],
    },
    {
      title: 'Documents',
      items: [
        { label: 'Devis',             icon: IconDoc,  path: '/workshop/quotations', badge: pendingQuotes, badgeColor: '#DFA23C' },
        { label: 'Factures',          icon: IconCard, path: '/workshop/invoices',   badge: unpaidInvoices, badgeColor: '#E2574B' },
        { label: 'Reçus de paiement', icon: IconReceipt, path: '/workshop/receipts' },
        { label: 'Bons de livraison', icon: IconHandover, path: '/workshop/delivery-notes' },
        { label: 'Bons de commande',  icon: IconBox,  path: '/workshop/purchase-orders' },
      ],
    },
    {
      // Parts sourcing from suppliers. The workshop asks, compares the replies,
      // and buys — the customer is billed later on the workshop's own devis.
      title: 'Fournisseurs',
      items: [
        { label: 'Commander des pièces', icon: IconBox, path: '/workshop/parts-request' },
        { label: 'Devis reçus',          icon: IconDoc, path: '/workshop/supplier-quotations', badge: supplierQuotes, badgeColor: '#DFA23C' },
      ],
    },
    {
      title: 'Gestion',
      items: [
        { label: 'Grand livre',           icon: IconLedger, path: '/workshop/ledger' },
        { label: 'Écritures financières', icon: IconBars,   path: '/workshop/finance' },
        { label: 'Catégories',            icon: IconTag,    path: '/workshop/categories' },
        { label: 'Profil atelier',        icon: IconUser,   path: '/workshop/profile' },
      ],
    },
  ];

  const linkClass = ({ isActive }) =>
    `relative flex items-center gap-[11px] px-2.5 py-[9px] rounded-[9px] text-[13px] transition-colors ${
      isActive
        ? 'bg-[#1E1B17] text-[#F4F1EA] font-medium'
        : 'text-[#9A9184] hover:bg-[#171512] hover:text-[#F4F1EA] font-normal'
    }`;

  return (
    <aside className={`w-[248px] h-full bg-[#100F0D] border-r border-[#211E1B] px-4 pt-[22px] pb-[18px]
                       flex flex-col gap-[22px] overflow-y-auto ${className}`}>

      <div className="flex items-center gap-[11px] px-1.5">
        <IparcMark size={38} />
        <div className="leading-[1.1]">
          <p className="font-['Space_Grotesk',sans-serif] text-sm font-semibold tracking-[.14em] text-[#F4F1EA]">IPARC</p>
          <p className="text-[9.5px] uppercase tracking-[.16em] text-[#6E665C] mt-0.5">Espace atelier</p>
        </div>
      </div>

      <div className="px-3 py-[11px] border border-[#241F1B] rounded-xl bg-gradient-to-b from-[#17140F] to-[#131110]">
        <p className="text-[9px] uppercase tracking-[.18em] text-[#6E665C] mb-[5px]">Atelier</p>
        <p className="font-['Space_Grotesk',sans-serif] text-[13.5px] font-semibold tracking-[-.01em] text-[#F4F1EA]">
          {user?.businessName || user?.name || 'Atelier'}
        </p>
        {(user?.city || user?.address) && (
          <p className="text-[11px] text-[#8F877B] mt-[3px] truncate">{user.city || user.address}</p>
        )}
      </div>

      <nav className="flex flex-col gap-[18px]">
        {sections.map(section => (
          <div key={section.title} className="flex flex-col gap-0.5">
            <p className="text-[9px] uppercase tracking-[.2em] text-[#5C554C] px-2.5 pb-[7px]">{section.title}</p>
            {section.items.map(item => {
              const Icon = item.icon;
              return (
                <NavLink key={item.path} to={item.path} className={linkClass}>
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span className="absolute left-0 top-[9px] bottom-[9px] w-0.5 rounded-sm bg-gradient-to-b from-[#F4F1EA] to-[#8A8780]" />
                      )}
                      <Icon className="w-[17px] h-[17px] shrink-0" />
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge > 0 && (
                        <span className="text-[10px] font-bold text-[#0D0C0B] rounded-full px-1.5 tabular-nums"
                              style={{ background: item.badgeColor }}>
                          {item.badge}
                        </span>
                      )}
                      {!item.badge && item.count != null && (
                        <span className="text-[10.5px] text-[#6E665C] tabular-nums">{item.count}</span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="mt-auto pt-3.5 border-t border-[#211E1B] flex flex-col gap-0.5">
        <NavLink to="/support"
          className="flex items-center gap-[11px] px-2.5 py-2 rounded-[9px] text-[12.5px] text-[#7C7468] hover:bg-[#171512] hover:text-[#F4F1EA] transition-colors">
          <IconHelp className="w-4 h-4" /> Aide &amp; support
        </NavLink>
        <button onClick={onLogout}
          className="flex items-center gap-[11px] px-2.5 py-2 rounded-[9px] text-[12.5px] text-[#7C7468] hover:bg-[#1E1512] hover:text-[#E2574B] transition-colors text-left">
          <IconLogout className="w-4 h-4" /> Déconnexion
        </button>
      </div>
    </aside>
  );
}
